import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  IconButton,
  Typography,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CloseIcon from "@mui/icons-material/Close";
// import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useState, useEffect } from "react";
// import CustomTooltip from "../../CustomTooltip";
import { apiDeleteRequest, apiGetRequest } from "../../../Helpers/Requests";
import endpoints from "../../../Helpers/Endpoints";
import { useSnackbar } from "../../SnackBar";
import CustomCheckboxLabel from "../../CustomCheckboxLabel";
import deleteAccountStyles from "./styles";
import { useTranslation } from "react-i18next";
import { createOnboardingAppUrl } from "../../../Helpers/Actions";

function DeleteAccountDialog({ openDialog = false, setOpenDialog }) {
  const [checked, setChecked] = useState(false);
  const [actionLoader, setActionLoader] = useState(false);
  const snackbar = useSnackbar();
  const [openOrders, setOpenOrders] = useState({});

  const { t } = useTranslation("delete-account");

  const getOpenOrders = async () => {
    try {
      const response = await apiGetRequest(endpoints.getOpenOrders);
      setOpenOrders(response?.data);
    } catch (err) {
      console.log("Open orders error:", err);
    }
  };

  useEffect(() => {
    if (openDialog) {
      getOpenOrders();
    }
  }, [openDialog]);

  const {
    dialogPaper,
    dialogTitle,
    closeIconButton,
    // helpIconBox,
    dialogContent,
    contentScrollBox,
    confirmText,
    warningText,
    warningBox,
    warningTitle,
    warningPoint,
    dialogActions,
    deleteButton,
    loader,
  } = deleteAccountStyles;

  const handleChange = (event) => {
    setChecked(event.target.checked);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setChecked(false);
  };

  const handleDelete = async () => {
    try {
      setActionLoader(true);
      await apiDeleteRequest(endpoints.deleteAccount);
      snackbar(t("account_deleted_successfully"), "success");
      handleClose();
      // Redirect to welcome page after a short delay to show the success message
      setTimeout(() => {
        window.location.href = createOnboardingAppUrl({ path: "/welcome" });
      }, 500);
    } catch (error) {
      console.log("error: ", error);
      // The server re-checks open orders at deletion time, so a refusal can
      // mean an order arrived after the dialog loaded its counts.
      await getOpenOrders();
      snackbar(t("account_deleted_failed"), "error");
    } finally {
      setActionLoader(false);
    }
  };

  const { businessOrdersCount, memberOrdersCount } = openOrders || {};

  return (
    <Dialog
      open={openDialog}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleClose();
      }}
      disableEscapeKeyDown
      PaperProps={{ sx: dialogPaper }}
    >
      <DialogTitle sx={dialogTitle}>{t("title")}</DialogTitle>

      <IconButton aria-label="close" onClick={handleClose} sx={closeIconButton}>
        <CloseIcon sx={{ color: "rgba(62, 63, 76, 1)", fontSize: "20px" }} />
      </IconButton>

      {/* <Box sx={helpIconBox}>
        <CustomTooltip title={t("help")}>
          <HelpOutlineIcon
            sx={{ color: "rgba(47, 43, 61, 1)", fontSize: "18px" }}
          />
        </CustomTooltip>
      </Box> */}

      <DialogContent sx={dialogContent}>
        <Box sx={contentScrollBox}>
          <Box>
            <Typography color="rgba(47, 43, 61, 0.9)" sx={confirmText}>
              {t("are_you_sure")}
            </Typography>
            <Typography color="rgba(47, 43, 61, 0.9)" sx={warningText}>
              {t("action_cannot_undone")}
            </Typography>
          </Box>

          {businessOrdersCount || memberOrdersCount ? (
            <Box sx={warningBox}>
              <Typography color="rgba(183, 72, 74, 1)" sx={warningTitle}>
                {t("please_complete_actions")}
              </Typography>
              <Box sx={{ mt: "8px" }}>
                {businessOrdersCount ? (
                  <Box sx={warningPoint}>
                    <Typography color="rgba(183, 72, 74, 1)" fontSize="15px">
                      1.
                    </Typography>
                    <Typography color="rgba(183, 72, 74, 1)" fontSize="15px">
                      {t("business_open_orders")} {businessOrdersCount}{" "}
                      {t("open_orders")}
                    </Typography>
                  </Box>
                ) : (
                  <></>
                )}
                {memberOrdersCount ? (
                  <Box sx={warningPoint}>
                    <Typography color="rgba(183, 72, 74, 1)" fontSize="15px">
                      2.
                    </Typography>
                    <Typography color="rgba(183, 72, 74, 1)" fontSize="15px">
                      {t("member_open_orders")} {memberOrdersCount}{" "}
                      {t("open_orders")}
                    </Typography>
                  </Box>
                ) : (
                  <></>
                )}
              </Box>
            </Box>
          ) : (
            <></>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={dialogActions}>
        <CustomCheckboxLabel
          label={t("confirm_deletion")}
          checked={checked}
          onChange={handleChange}
          disabled={businessOrdersCount || memberOrdersCount}
        />
        <Button
          onClick={handleDelete}
          disabled={!checked || businessOrdersCount || memberOrdersCount}
          sx={deleteButton}
        >
          {t("delete_account")}
          {actionLoader && <CircularProgress size={20} sx={loader} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeleteAccountDialog;
